import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Pagination} from "@models/pagination.model";
import {ToastrService} from "ngx-toastr";
import {Patient} from "@shared/api/models/query/patient.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {DragDropModule} from "@angular/cdk/drag-drop";
import {CareProgramsService} from "@shared/api/services/v2/care-programs.service";
import {CareProgram} from "@shared/api/models/query/care-program.model";
import {
    PatientRegistrationCareProgrammeBlockComponent
} from "@cdss/pages/patient-registration/care-programme/patient-registration-care-programme-block.component";
import {NgIf} from "@angular/common";
import {UserCareProgramsService} from "@shared/api/services/v2/user-care-programs.service";
import {UserCareProgram} from "@shared/api/models/query/user-care-program.model";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";

@Component({
    selector: 'patient-registration-care-programme',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        DragDropModule,
        PatientRegistrationCareProgrammeBlockComponent,
        NgIf,
        SpinnerComponent
    ],
    templateUrl: './patient-registration-care-programme.component.html',
    standalone: true,
    styleUrl: './patient-registration-care-programme.component.scss'
})
export class PatientRegistrationCareProgrammeComponent implements OnInit {
    @Output() output = new EventEmitter<string>();

    @ViewChild("patientBlock") patientCareProgrammeBlockComponent: PatientRegistrationCareProgrammeBlockComponent
    @ViewChild("carerBlock") carerCareProgrammeBlockComponent: PatientRegistrationCareProgrammeBlockComponent

    loading: boolean = false
    disabled: boolean = false
    pagination: Pagination = new Pagination()
    typeOfUser: string
    patient: Patient

    patientCarePrograms: CareProgram[]
    patientUserCareProgram?: UserCareProgram = null

    carerCarePrograms: CareProgram[]
    carerUserCareProgram?: UserCareProgram = null

    constructor(
        private toastr: ToastrService,
        private patientsService: PatientsService,
        private careProgramsService: CareProgramsService,
        private userCareProgramsService: UserCareProgramsService
    ) {
    }

    ngOnInit(): void {

    }

    async init(patient: Patient, typeOfUser: string, disabled: boolean = false, organisationId?: number) {
        this.disabled = disabled
        this.loading = true
        this.patient = patient
        this.typeOfUser = typeOfUser

        if (organisationId) {
            const isPatient = ['Patient', 'Patient and carer'].includes(typeOfUser);
            const isCarer = ['Carer', 'Patient and carer'].includes(typeOfUser);
            const [patientResponse, carerResponse] = await Promise.all([
                isPatient ? this.careProgramsService.listByOrganisationAndTarget(organisationId, 'patient') : Promise.resolve(null),
                isCarer ? this.careProgramsService.listByOrganisationAndTarget(organisationId, 'carer') : Promise.resolve(null),
            ]);
            this.patientCarePrograms = patientResponse?.data ?? [];
            this.carerCarePrograms = carerResponse?.data ?? [];
        } else {
            const response = await this.careProgramsService.listAll()
            this.patientCarePrograms = response.data.filter((careProgram: CareProgram) => {
                return careProgram.target == "patient"
            })
            this.carerCarePrograms = response.data.filter((careProgram: CareProgram) => {
                return careProgram.target == "carer"
            })
        }

        if (['Patient', 'Patient and carer'].includes(this.typeOfUser)) {
            const pagination = new Pagination()
            pagination.filter = {
                user: patient.user.id,
            }
            this.userCareProgramsService.listPaginated(pagination).subscribe({
                next: (response) => {
                    if (response.data.length > 0) {
                        this.patientUserCareProgram = response.data[0]
                        this.patientCareProgrammeBlockComponent.setUserCareProgram(this.patientUserCareProgram)
                    }
                }
            })
        }
        if (['Carer', 'Patient and carer'].includes(this.typeOfUser)) {
            const pagination = new Pagination()
            pagination.filter = {
                user: patient.carer.user.id,
            }
            this.userCareProgramsService.listPaginated(pagination).subscribe({
                next: (response) => {
                    if (response.data.length > 0) {
                        this.carerUserCareProgram = response.data[0]
                        this.carerCareProgrammeBlockComponent.setUserCareProgram(this.carerUserCareProgram)
                    }
                }
            })
        }
        this.loading = false
    }

    processForm(draft: boolean) {
        let payload = {}
        if (this.typeOfUser == 'Patient') {
            this.patientCareProgrammeBlockComponent.showError = true
            if (!this.patientCareProgrammeBlockComponent.selectedCareProgram) {
                this.toastr.error('Select a care programme');
                return
            }
            const specification = this.patientCareProgrammeBlockComponent.getResult()
            if (!specification) {
                this.toastr.error('All weeks must have one chapter allocated at least');
                return
            }
            payload = {
                wizardStep: 4,
                careProgramId: this.patientCareProgrammeBlockComponent.selectedCareProgram.id,
                careProgramSpecification: specification,
            }
        }
        if (this.typeOfUser == 'Carer') {
            this.carerCareProgrammeBlockComponent.showError = true
            if (!this.carerCareProgrammeBlockComponent.selectedCareProgram) {
                this.toastr.error('Select a care programme');
                return
            }
            const specification = this.carerCareProgrammeBlockComponent.getResult()
            if (!specification) {
                this.toastr.error('All weeks must have one chapter allocated at least');
                return
            }
            if (!this.carerCareProgrammeBlockComponent.validateChild()) {
                this.toastr.error('Complete the required details in the "Your child" section');
                return
            }
            payload = {
                wizardStep: 4,
                carerCareProgramId: this.carerCareProgrammeBlockComponent.selectedCareProgram.id,
                carerCareProgramSpecification: specification,
                // UNIVAPLAT-3719: empty when the programme has no child mode.
                ...this.carerCareProgrammeBlockComponent.getChildPayload(),
            }
        }
        if (this.typeOfUser == 'Patient and carer') {
            this.patientCareProgrammeBlockComponent.showError = true
            this.carerCareProgrammeBlockComponent.showError = true
            if (!this.patientCareProgrammeBlockComponent.selectedCareProgram || !this.carerCareProgrammeBlockComponent.selectedCareProgram) {
                this.toastr.error('Select a care programme');
                return
            }
            const patientSpecification = this.patientCareProgrammeBlockComponent.getResult()
            if (!patientSpecification) {
                this.toastr.error('All weeks must have one chapter allocated at least');
                return
            }
            this.carerCareProgrammeBlockComponent.showError = true
            const carerSpecification = this.carerCareProgrammeBlockComponent.getResult()
            if (!carerSpecification) {
                this.toastr.error('All weeks must have one chapter allocated at least');
                return
            }
            payload = {
                wizardStep: 4,
                careProgramId: this.patientCareProgrammeBlockComponent.selectedCareProgram.id,
                careProgramSpecification: patientSpecification,
                carerCareProgramId: this.carerCareProgrammeBlockComponent.selectedCareProgram.id,
                carerCareProgramSpecification: carerSpecification,
            }
        }

        this.patientsService.update(this.patient.id, payload).subscribe({
            next: (response) => {
                this.toastr.success('User updated successfully', 'Success');
                this.output.emit(draft? "draft" : "next")
            },
            error: (error) => {
                this.toastr.error(error.error.error);
            }
        })
    }

    protected prev() {
        this.output.emit("prev")
    }
}
